<script lang="ts" setup>
  import { InlineAlert, InlineAlertVariant } from '@tundr/ds-components';
  import { useLocale } from '@tundr/i18n';
  import { useTemplateRef } from 'vue';
  import type { GeoPosition } from '../api/geo.types.ts';
  import { useIsMapAvailable } from './geo.config.ts';
  import { mapClass } from './LocationMap.css.ts';
  import { LocationMapStatus, useLocationMap } from './useLocationMap.ts';

  type LocationMapProps = {
    position: GeoPosition | null;
    ariaLabel?: string;
    testId?: string;
  };

  const props = withDefaults(defineProps<LocationMapProps>(), {
    testId: 'location-map',
  });

  const emit = defineEmits<{
    error: [reason: unknown];
  }>();

  const { t } = useLocale('geo');

  const isMapAvailable = useIsMapAvailable();
  const container = useTemplateRef<HTMLElement>('container');

  const { status } = useLocationMap({
    container,
    position: () => props.position,
    markerTitle: () => t('location_map.marker'),
    onError: (reason) => emit('error', reason),
  });
</script>

<template>
  <div v-if="isMapAvailable" :data-testid="testId">
    <InlineAlert
      v-if="status === LocationMapStatus.ERROR"
      :variant="InlineAlertVariant.ERROR"
      :text="t('location_map.unavailable')"
      :test-id="`${testId}-unavailable`"
    />
    <div
      v-else
      ref="container"
      role="region"
      :class="mapClass"
      :aria-label="ariaLabel ?? t('location_map.label')"
      :aria-busy="status === LocationMapStatus.LOADING || undefined"
      :data-testid="`${testId}-canvas`"
    />
  </div>
</template>
